import api from "@/lib/axios";

export const isNewsletterOptedIn = (value) =>
  value === true || value === 1 || value === "1";

const recordWithOptIn = (obj) =>
  obj && typeof obj === "object" && ("email_opt_in" in obj || "sms_opt_in" in obj)
    ? obj
    : null;

const pickNewsletterRecord = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  return (
    recordWithOptIn(payload.newsletter) ||
    recordWithOptIn(payload.application?.newsletter) ||
    recordWithOptIn(payload.application?.candidate?.newsletter) ||
    recordWithOptIn(payload.candidate?.newsletter) ||
    recordWithOptIn(payload.data?.newsletter) ||
    recordWithOptIn(payload.data) ||
    recordWithOptIn(payload.application?.candidate) ||
    recordWithOptIn(payload.candidate) ||
    recordWithOptIn(payload.application) ||
    recordWithOptIn(payload)
  );
};

export const extractNewsletterPrefs = (payload) => {
  const newsletter = pickNewsletterRecord(payload);

  return {
    hasPrefs: Boolean(newsletter),
    emailOptIn: isNewsletterOptedIn(newsletter?.email_opt_in),
    smsOptIn: isNewsletterOptedIn(newsletter?.sms_opt_in),
  };
};

export const newsletterService = {
  /** Admin: list opted-in marketing contacts */
  getSubscribers: async (params = {}) => {
    try {
      const response = await api.get('/newsletters', { params });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message || 'Failed to fetch marketing contacts');
    }
  },

  /** Candidate filter tag options for marketing campaign targeting */
  getFilterTags: async () => {
    try {
      const response = await api.get("/filter-tags");
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message || "Failed to fetch filter tags");
    }
  },

  /** Public apply flow: existing preferences for this application/email */
  getApplicationOptIn: async (applicationId, email) => {
    try {
      const response = await api.get(`/applications/${applicationId}/newsletter`, {
        params: { email },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message || "Failed to fetch marketing preferences");
    }
  },

  /** Post-application opt-in (public apply flow, no auth required) */
  updateApplicationOptIn: async (applicationId, { email, email_opt_in, sms_opt_in }) => {
    try {
      const response = await api.post(`/applications/${applicationId}/newsletter`, {
        email,
        email_opt_in: email_opt_in ? 1 : 0,
        sms_opt_in: sms_opt_in ? 1 : 0,
      });
      return response;
    } catch (error) {
      if (error.response) {
        const apiError = error.response.data;
        const message = apiError.message || 'Failed to save marketing preferences';
        const throwableError = new Error(message);
        throwableError.errors = apiError.errors || {};
        throw throwableError;
      }
      throw new Error(error.message);
    }
  },

  /** Logged-in candidate: fetch newsletter preferences */
  getPreferences: async (userId) => {
    try {
      const response = await api.get(`/candidate/${userId}/newsletter`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch marketing preferences: " + error.message);
    }
  },

  /** Logged-in candidate: update newsletter preferences */
  updatePreferences: async (userId, { email_opt_in, sms_opt_in }) => {
    try {
      const response = await api.put(`/candidate/${userId}/newsletter`, {
        email_opt_in: email_opt_in ? 1 : 0,
        sms_opt_in: sms_opt_in ? 1 : 0,
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      throw new Error(message || "Failed to update marketing preferences");
    }
  },
};
